import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import GlobalStyles from "../../../Resources/Styles";
import { useDevice, useScreenDimensions } from "../../_layout";
import Exercise from "../../Firestore/Exercise";
import { useTranslation } from "react-i18next";
import ToggleSwitch from "../../Components/ToggleSwitch";
import Tooltip from "../../Components/Tooltip";
import { useToast } from "../../contexts/ToastContext";
import { BlockPlanificationConstants } from "../../../Resources/Constants";

// Grid layout constants
const CELL_SIZE = 36;
const WORKOUT_LABEL_WIDTH_EN = 80;
const WORKOUT_LABEL_WIDTH_ES = 120;

const DuplicateExerciseModal = observer(
  ({ isVisible, onClose, exercise, block }) => {
    const { t, i18n } = useTranslation();
    const { isMobile } = useDevice();
    const { screenSize } = useScreenDimensions();
    const { showToast } = useToast();
    const scrollViewRef = useRef(null);

    const [selectedWorkouts, setSelectedWorkouts] = useState(new Set());
    const [keepSameOrder, setKeepSameOrder] = useState(false);
    const [closeHovered, setCloseHovered] = useState(false);
    const [hoveredCell, setHoveredCell] = useState(null);
    const [clearAllHovered, setClearAllHovered] = useState(false);
    const [confirmHovered, setConfirmHovered] = useState(false);

    const toUppercase = (value) =>
      typeof value === "string" ? value.toUpperCase() : value;

    // Get workout label width based on language
    const getWorkoutLabelWidth = () => {
      const lang = i18n.language || "en";
      return lang.startsWith("es")
        ? WORKOUT_LABEL_WIDTH_ES
        : WORKOUT_LABEL_WIDTH_EN;
    };

    // Split workout label into word and number parts
    const getWorkoutLabelParts = (number) => {
      const fullText = t("workoutWithNumber-string", { number });
      // The format is "Workout #1" or "Entrenamiento #1"
      // Split by "#" to separate word from number
      const parts = fullText.split("#");
      if (parts.length === 2) {
        return {
          word: parts[0].trim(),
          number: `#${parts[1].trim()}`,
        };
      }
      // Fallback if format is unexpected
      return {
        word: fullText,
        number: "",
      };
    };

    // Reset selection when modal opens
    useEffect(() => {
      if (!isVisible) return;
      setSelectedWorkouts(new Set());
      setKeepSameOrder(false);
    }, [isVisible]);

    // Get all weeks
    const weeks = useMemo(() => {
      if (!block?.totalWeeks) return [];
      return Array.from({ length: block.totalWeeks }, (_, i) => i + 1);
    }, [block?.totalWeeks]);

    // Get workout rows based on sorted position within each week
    // Row 0 = 1st workout of each week, Row 1 = 2nd workout of each week, etc.
    const workoutRows = useMemo(() => {
      if (!block?.workouts) return [];

      // Group workouts by week and sort each week's workouts by order
      const workoutsByWeek = new Map();

      block.workouts.forEach((workout) => {
        const week = workout.week ?? 1;
        if (!workoutsByWeek.has(week)) {
          workoutsByWeek.set(week, []);
        }
        workoutsByWeek.get(week).push(workout);
      });

      // Sort each week's workouts by order
      workoutsByWeek.forEach((workouts) => {
        workouts.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      });

      // Find the maximum number of workouts in any week
      let maxWorkoutsInAnyWeek = 0;
      workoutsByWeek.forEach((workouts) => {
        if (workouts.length > maxWorkoutsInAnyWeek) {
          maxWorkoutsInAnyWeek = workouts.length;
        }
      });

      if (maxWorkoutsInAnyWeek === 0) return [];

      // Create rows - each row index represents the Nth workout in each week
      const rows = [];
      for (let rowIndex = 0; rowIndex < maxWorkoutsInAnyWeek; rowIndex++) {
        const workoutsMap = new Map();

        // For each week, get the workout at this position (if it exists)
        weeks.forEach((week) => {
          const weekWorkouts = workoutsByWeek.get(week) || [];
          if (rowIndex < weekWorkouts.length) {
            workoutsMap.set(week, weekWorkouts[rowIndex]);
          }
        });

        rows.push({
          rowIndex,
          workouts: workoutsMap,
        });
      }

      return rows;
    }, [block?.workouts, weeks]);

    const workoutPositionMap = useMemo(() => {
      const map = new Map();
      if (!block?.workouts) return map;
      const workoutsByWeek = new Map();

      block.workouts.forEach((workout) => {
        const week = workout?.week ?? 1;
        if (!workoutsByWeek.has(week)) {
          workoutsByWeek.set(week, []);
        }
        workoutsByWeek.get(week).push(workout);
      });

      workoutsByWeek.forEach((workouts) => {
        workouts.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        workouts.forEach((workout, index) => {
          if (workout?.id) {
            map.set(workout.id, index + 1);
          }
        });
      });

      return map;
    }, [block?.workouts]);

    // Get the current workout of the exercise to exclude from selection
    const currentWorkoutId = exercise?.workout?.id;
    const sourceProtocolSlot = exercise?.protocolSlot ?? null;
    const sourceIsSecondaryProtocol = exercise?.isSecondaryProtocol ?? false;

    const blockedWeeks = useMemo(() => {
      const blocked = new Set();
      if (!sourceProtocolSlot || !block?.workouts) return blocked;

      if (sourceIsSecondaryProtocol) {
        // For secondary protocol exercises, group workouts by week first
        // A week should only be blocked if NO workout in that week can accept the secondary protocol
        const workoutsByWeek = new Map();

        block.workouts.forEach((workout) => {
          const week = workout?.week ?? 1;
          if (!workoutsByWeek.has(week)) {
            workoutsByWeek.set(week, []);
          }
          workoutsByWeek.get(week).push(workout);
        });

        // For each week, check if ANY workout can accept the secondary protocol
        workoutsByWeek.forEach((workouts, week) => {
          let canAcceptSecondary = false;

          for (const workout of workouts) {
            const exercises = Array.isArray(workout?.exercises)
              ? workout.exercises
              : [];
            const hasPrimary = exercises.some(
              (existingExercise) =>
                (existingExercise?.protocolSlot ?? null) ===
                  sourceProtocolSlot &&
                !(existingExercise?.isSecondaryProtocol ?? false),
            );
            const hasSecondary = exercises.some(
              (existingExercise) =>
                (existingExercise?.protocolSlot ?? null) ===
                  sourceProtocolSlot &&
                (existingExercise?.isSecondaryProtocol ?? false),
            );

            // A workout can accept secondary if it has primary and no secondary
            if (hasPrimary && !hasSecondary) {
              canAcceptSecondary = true;
              break; // Found at least one workout that can accept it
            }
          }

          // Block the week only if NO workout can accept the secondary protocol
          if (!canAcceptSecondary) {
            blocked.add(week);
          }
        });
      } else {
        // For primary protocol exercises, block weeks that have a primary protocol
        block.workouts.forEach((workout) => {
          const week = workout?.week ?? 1;
          const exercises = Array.isArray(workout?.exercises)
            ? workout.exercises
            : [];
          const hasPrimary = exercises.some(
            (existingExercise) =>
              (existingExercise?.protocolSlot ?? null) === sourceProtocolSlot &&
              !(existingExercise?.isSecondaryProtocol ?? false),
          );

          if (hasPrimary) {
            blocked.add(week);
          }
        });
      }

      return blocked;
    }, [block?.workouts, sourceProtocolSlot, sourceIsSecondaryProtocol]);

    const hasProtocolSlotVariant = useMemo(() => {
      if (!sourceProtocolSlot || !block?.workouts) return false;
      return block.workouts.some((workout) =>
        Array.isArray(workout?.exercises)
          ? workout.exercises.some(
              (existingExercise) =>
                (existingExercise?.protocolSlot ?? null) ===
                  sourceProtocolSlot &&
                (existingExercise?.isSecondaryProtocol ?? false) !==
                  sourceIsSecondaryProtocol,
            )
          : false,
      );
    }, [block?.workouts, sourceProtocolSlot, sourceIsSecondaryProtocol]);

    const blockedWeeksList = useMemo(() => {
      if (blockedWeeks.size === 0) return [];
      return Array.from(blockedWeeks).sort((a, b) => a - b);
    }, [blockedWeeks]);

    // Track which individual workouts should be blocked
    // When a complementary exercise exists in a week, block all workouts except the one containing it
    const blockedWorkouts = useMemo(() => {
      const blocked = new Set();
      if (!sourceProtocolSlot || !block?.workouts) return blocked;

      // Group workouts by week
      const workoutsByWeek = new Map();
      block.workouts.forEach((workout) => {
        const week = workout?.week ?? 1;
        if (!workoutsByWeek.has(week)) {
          workoutsByWeek.set(week, []);
        }
        workoutsByWeek.get(week).push(workout);
      });

      // For each week, check if any workout contains a complementary exercise
      workoutsByWeek.forEach((workouts, week) => {
        let workoutWithComplementary = null;

        for (const workout of workouts) {
          const exercises = Array.isArray(workout?.exercises)
            ? workout.exercises
            : [];

          if (sourceIsSecondaryProtocol) {
            // When duplicating SECONDARY: Find workouts with PRIMARY protocol (same slot)
            const hasPrimary = exercises.some(
              (existingExercise) =>
                (existingExercise?.protocolSlot ?? null) ===
                  sourceProtocolSlot &&
                !(existingExercise?.isSecondaryProtocol ?? false),
            );
            if (hasPrimary) {
              workoutWithComplementary = workout;
              break;
            }
          } else {
            // When duplicating PRIMARY: Find workouts with SECONDARY protocol (same slot)
            const hasSecondary = exercises.some(
              (existingExercise) =>
                (existingExercise?.protocolSlot ?? null) ===
                  sourceProtocolSlot &&
                (existingExercise?.isSecondaryProtocol ?? false),
            );
            if (hasSecondary) {
              workoutWithComplementary = workout;
              break;
            }
          }
        }

        // If a complementary exercise exists in this week, block all workouts except the one containing it
        if (workoutWithComplementary && workoutWithComplementary.id) {
          workouts.forEach((workout) => {
            if (workout?.id && workout.id !== workoutWithComplementary.id) {
              blocked.add(workout.id);
            }
          });
        }
      });

      return blocked;
    }, [block?.workouts, sourceProtocolSlot, sourceIsSecondaryProtocol]);

    // Track weeks that have at least one selected workout (for protocol slot exercises)
    // This ensures only one workout per week can be selected when duplicating protocol slot exercises
    const weeksWithSelectedWorkouts = useMemo(() => {
      const weeksSet = new Set();
      if (!sourceProtocolSlot || selectedWorkouts.size === 0) return weeksSet;

      block?.workouts?.forEach((workout) => {
        if (workout?.id && selectedWorkouts.has(workout.id)) {
          const week = workout?.week ?? 1;
          weeksSet.add(week);
        }
      });

      return weeksSet;
    }, [sourceProtocolSlot, selectedWorkouts, block?.workouts]);

    const persistBlockChange = () => {
      const normalizedId = String(block?.id ?? "").toLowerCase();
      if (block?.client) {
        block.client.updateClientTransactionData(normalizedId);
        block.client.startOrResetBatchTimer();
      } else if (block?.coach) {
        block.coach.updateCoachTransactionBlocks(normalizedId);
        block.coach.startOrResetBatchTimer();
      }
    };

    const getWorkoutLabel = (workout) => {
      if (!workout) return "";
      const week = workout?.week ?? 1;
      const customWeekName = block?.getWeekName?.(week) || "";
      const weekLabel =
        customWeekName.trim() !== ""
          ? customWeekName
          : t("weekWithNumber-string", { week });
      const workoutName =
        typeof workout?.name === "string" ? workout.name.trim() : "";
      const workoutNumber =
        workoutPositionMap.get(workout?.id) ??
        (typeof workout?.order === "number" ? workout.order + 1 : 1);
      const workoutLabel =
        workoutName ||
        t("workoutWithNumber-string", {
          number: workoutNumber,
        });
      return `${weekLabel} · ${workoutLabel}`;
    };

    const toggleWorkoutSelection = (workoutId, week) => {
      if (workoutId === currentWorkoutId) return;
      if (sourceProtocolSlot && blockedWeeks.has(week)) return;
      if (sourceProtocolSlot && blockedWorkouts.has(workoutId)) return;

      // For protocol slot exercises, only allow one workout per week
      if (sourceProtocolSlot) {
        // Check if this week already has a selected workout (and it's not the one being toggled)
        const weekHasSelectedWorkout = weeksWithSelectedWorkouts.has(week);
        const isCurrentlySelected = selectedWorkouts.has(workoutId);

        // If week already has a selected workout and we're trying to select a different one, block it
        if (weekHasSelectedWorkout && !isCurrentlySelected) {
          return;
        }
      }

      setSelectedWorkouts((prev) => {
        const next = new Set(prev);
        if (next.has(workoutId)) {
          next.delete(workoutId);
        } else {
          next.add(workoutId);
        }
        return next;
      });
    };

    const toggleRowAllSelection = (workoutsMap) => {
      const validWorkouts = [];
      const workoutsByWeek = new Map();

      // Collect all valid workouts, grouped by week
      workoutsMap.forEach((workout, week) => {
        const workoutId = workout?.id;
        const isBlocked = sourceProtocolSlot && blockedWeeks.has(week);
        const isBlockedWorkout =
          sourceProtocolSlot && blockedWorkouts.has(workoutId);
        const isCurrent = workoutId === currentWorkoutId;
        const weekHasSelectedWorkout =
          sourceProtocolSlot && weeksWithSelectedWorkouts.has(week);
        const isBlockedBySelection =
          sourceProtocolSlot &&
          weekHasSelectedWorkout &&
          !selectedWorkouts.has(workoutId);

        if (
          workoutId &&
          !isBlocked &&
          !isBlockedWorkout &&
          !isCurrent &&
          !isBlockedBySelection
        ) {
          if (!workoutsByWeek.has(week)) {
            workoutsByWeek.set(week, []);
          }
          workoutsByWeek.get(week).push({ id: workoutId, week });
        }
      });

      // For protocol slot exercises, select one workout per week (first valid one)
      // For non-protocol slot exercises, select all valid workouts
      if (sourceProtocolSlot) {
        workoutsByWeek.forEach((weekWorkouts) => {
          // Only add the first valid workout from each week
          if (weekWorkouts.length > 0) {
            validWorkouts.push(weekWorkouts[0]);
          }
        });
      } else {
        // For non-protocol slot exercises, add all valid workouts
        workoutsByWeek.forEach((weekWorkouts) => {
          validWorkouts.push(...weekWorkouts);
        });
      }

      if (validWorkouts.length === 0) return;

      // Check if all valid workouts are already selected
      const allSelected = validWorkouts.every((w) =>
        selectedWorkouts.has(w.id),
      );

      runInAction(() => {
        setSelectedWorkouts((prev) => {
          const next = new Set(prev);
          if (allSelected) {
            // Unselect all valid workouts
            validWorkouts.forEach((w) => next.delete(w.id));
          } else {
            // Select all valid workouts
            validWorkouts.forEach((w) => next.add(w.id));
          }
          return next;
        });
      });
    };

    const duplicateExerciseToWorkout = (
      targetWorkout,
      preserveOrder = false,
    ) => {
      if (!exercise || !targetWorkout) return;

      const existingExercises = targetWorkout.exercises || [];
      const sourceOrder = exercise.order ?? 0;

      let newOrder;
      if (preserveOrder) {
        // Insert at the same order position, shifting existing exercises down
        existingExercises.forEach((ex) => {
          if ((ex.order ?? 0) >= sourceOrder) {
            ex.setProperty?.("order", (ex.order ?? 0) + 1);
          }
        });
        newOrder = sourceOrder;
      } else {
        // Append at the end
        const maxOrder =
          existingExercises.length > 0
            ? Math.max(...existingExercises.map((e) => e.order ?? 0))
            : -1;
        newOrder = maxOrder + 1;
      }

      const newExercise = new Exercise(
        uuidv4(),
        exercise.name,
        exercise.originalName,
        exercise.notes,
        newOrder,
        true,
        false,
        targetWorkout,
        [],
        [],
        undefined,
        undefined,
        exercise.link,
      );

      newExercise.setProperty("protocolSlot", exercise.protocolSlot);
      newExercise.setProperty("protocolText", exercise.protocolText);
      newExercise.setProperty(
        "isSecondaryProtocol",
        exercise.isSecondaryProtocol,
      );

      // Copy series (without reps, RPE, and weight - only copy other properties)
      if (Array.isArray(exercise.series)) {
        exercise.series.forEach((serie) => {
          // copySerie with keepActual=false will not copy reps, rpe, weight
          // but will copy targetReps, targetRpe, targetWeight, relevance, requireVideo
          newExercise.copySerie(serie, false, "target");
        });
      }

      // Copy attributes
      if (Array.isArray(exercise.attributes)) {
        exercise.attributes.forEach((attribute) => {
          // copyAttribute preserves isPredefined and resets isSelected to false
          newExercise.copyAttribute(attribute);
        });
      }

      targetWorkout.exercises.push(newExercise);
      targetWorkout.setProperty?.("hasChanged", true);
    };

    const handleDuplicate = () => {
      if (!block || selectedWorkouts.size === 0) return;

      runInAction(() => {
        selectedWorkouts.forEach((workoutId) => {
          const targetWorkout = block.workouts.find((w) => w.id === workoutId);
          const targetWeek = targetWorkout?.week ?? 1;
          if (sourceProtocolSlot && blockedWeeks.has(targetWeek)) {
            return;
          }
          if (targetWorkout) {
            duplicateExerciseToWorkout(targetWorkout, keepSameOrder);
          }
        });
      });

      persistBlockChange();

      const exerciseName =
        exercise?.getName?.(t, i18n.language) ||
        exercise?.name ||
        t("exercise-string");
      const targetWorkouts = block.workouts.filter((workout) =>
        selectedWorkouts.has(workout?.id),
      );
      const workoutLabels = targetWorkouts
        .map((workout) => getWorkoutLabel(workout))
        .filter((label) => label)
        .join(", ");

      showToast("duplicateExerciseToastTitle-string", {
        type: "info",
        title: "duplicateExerciseToastTitle-string",
        description: "duplicateExerciseToastDescription-string",
        duration: BlockPlanificationConstants.TOAST_DURATION_MS,
        variables: {
          exerciseName,
          workouts: workoutLabels,
        },
      });

      onClose?.();
    };

    const hasSelection = selectedWorkouts.size > 0;
    const exerciseName = exercise?.name || t("exercise-string");
    const protocolLabel = sourceProtocolSlot ? t(sourceProtocolSlot) : "";
    const protocolSlotLabel = sourceProtocolSlot
      ? hasProtocolSlotVariant
        ? `${protocolLabel} #${sourceIsSecondaryProtocol ? 2 : 1}`
        : protocolLabel
      : "";
    const blockedWeeksLabel = blockedWeeksList
      .map((week) => t("weekAbbr-string", { week }))
      .join(", ");

    // Calculate grid dimensions
    return (
      <Modal
        animationType="fade"
        transparent={true}
        visible={isVisible}
        onRequestClose={onClose}
      >
        <View style={GlobalStyles.centeredView}>
          <TouchableOpacity
            onPress={onClose}
            style={GlobalStyles.touchableBackground}
          />
          <View
            style={[
              styles.modalView,
              isMobile && { maxHeight: "90%" },
              screenSize === "small" && { maxHeight: "95%" },
            ]}
          >
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              onMouseEnter={() => !isMobile && setCloseHovered(true)}
              onMouseLeave={() => setCloseHovered(false)}
            >
              <Ionicons
                name="close"
                size={22}
                color={closeHovered ? Colors.blackColor : Colors.grayColor}
              />
            </TouchableOpacity>

            <View style={{ gap: 8, marginBottom: 16, width: "100%" }}>
              <Text style={styles.title}>
                {toUppercase(t("duplicateExerciseTitle-string"))}
              </Text>
              <Text style={styles.subtitle}>
                {exerciseName}
                {protocolSlotLabel ? ` · ${protocolSlotLabel}` : ""}
              </Text>
            </View>

            <View
              style={[
                styles.gridSectionContainer,
                isMobile && styles.gridSectionContainerMobile,
              ]}
            >
              <Text style={styles.sectionLabel}>
                {t("duplicateExerciseSelectWorkouts-string")}
              </Text>

              <ScrollView
                style={[
                  styles.verticalScrollView,
                  isMobile && styles.verticalScrollViewMobile,
                ]}
                contentContainerStyle={styles.verticalScrollContent}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
              >
                <ScrollView
                  ref={scrollViewRef}
                  horizontal
                  showsHorizontalScrollIndicator={true}
                  style={styles.gridScrollView}
                  contentContainerStyle={[
                    styles.gridScrollContent,
                    styles.gridScrollContentCentered,
                  ]}
                  nestedScrollEnabled={true}
                >
                  <View style={styles.gridContainer}>
                    {/* Header Row - Week Labels */}
                    <View style={styles.gridHeaderRow}>
                      <View
                        style={[
                          styles.cornerCell,
                          { width: getWorkoutLabelWidth() },
                        ]}
                      />
                      {/* Select All Column Header (Empty) */}
                      <View
                        style={[styles.weekHeaderCell, { width: CELL_SIZE }]}
                      />
                      {weeks.map((week) => (
                        <View
                          key={`week-${week}`}
                          style={[styles.weekHeaderCell, { width: CELL_SIZE }]}
                        >
                          <Text style={styles.weekHeaderText}>
                            {t("weekAbbr-string", { week })}
                          </Text>
                        </View>
                      ))}
                    </View>

                    {/* Workout Rows */}
                    {workoutRows.map((row) => {
                      // Calculate row selection state
                      // For protocol slot exercises, only consider one workout per week
                      // For non-protocol slot exercises, consider all valid workouts
                      const validWorkoutsByWeek = new Map();
                      let hasSelectable = false;

                      row.workouts.forEach((workout, week) => {
                        const workoutId = workout?.id;
                        const isBlocked =
                          sourceProtocolSlot && blockedWeeks.has(week);
                        const isBlockedWorkout =
                          sourceProtocolSlot && blockedWorkouts.has(workoutId);
                        const isCurrent = workoutId === currentWorkoutId;
                        const weekHasSelectedWorkout =
                          sourceProtocolSlot &&
                          weeksWithSelectedWorkouts.has(week);
                        const isBlockedBySelection =
                          sourceProtocolSlot &&
                          weekHasSelectedWorkout &&
                          !selectedWorkouts.has(workoutId);

                        if (
                          workoutId &&
                          !isBlocked &&
                          !isBlockedWorkout &&
                          !isCurrent &&
                          !isBlockedBySelection
                        ) {
                          hasSelectable = true;
                          if (!validWorkoutsByWeek.has(week)) {
                            validWorkoutsByWeek.set(week, []);
                          }
                          validWorkoutsByWeek.get(week).push(workoutId);
                        }
                      });

                      // For protocol slot exercises, only consider one workout per week (first valid one)
                      // For non-protocol slot exercises, consider all valid workouts
                      const workoutsToConsider = [];
                      validWorkoutsByWeek.forEach((workoutIds, week) => {
                        if (sourceProtocolSlot) {
                          // Only consider the first valid workout per week
                          workoutsToConsider.push(workoutIds[0]);
                        } else {
                          // Consider all valid workouts
                          workoutsToConsider.push(...workoutIds);
                        }
                      });

                      // Calculate selection state based on workouts to consider
                      let allSelected = true;
                      let someSelected = false;
                      workoutsToConsider.forEach((workoutId) => {
                        if (!selectedWorkouts.has(workoutId)) {
                          allSelected = false;
                        } else {
                          someSelected = true;
                        }
                      });

                      const isAllSelected = hasSelectable && allSelected;
                      const isIndeterminate =
                        hasSelectable && someSelected && !allSelected;
                      const isSelectAllDisabled = !hasSelectable;
                      const selectAllKey = `${row.rowIndex}-all`;
                      const isSelectAllHovered = hoveredCell === selectAllKey;

                      return (
                        <View
                          key={`row-${row.rowIndex}`}
                          style={styles.gridRow}
                        >
                          <View
                            style={[
                              styles.workoutLabelCell,
                              { width: getWorkoutLabelWidth() },
                            ]}
                          >
                            {(() => {
                              const labelParts = getWorkoutLabelParts(
                                row.rowIndex + 1,
                              );
                              return (
                                <View style={styles.workoutLabelContainer}>
                                  <Text
                                    style={styles.workoutLabelWord}
                                    numberOfLines={1}
                                    adjustsFontSizeToFit={true}
                                    minimumFontScale={0.7}
                                  >
                                    {labelParts.word}
                                  </Text>
                                  <Text
                                    style={styles.workoutLabelNumber}
                                    numberOfLines={1}
                                  >
                                    {labelParts.number}
                                  </Text>
                                </View>
                              );
                            })()}
                          </View>

                          {/* Select All Column */}
                          <View style={[styles.gridCell, { width: CELL_SIZE }]}>
                            <Tooltip
                              content={
                                isAllSelected
                                  ? "unselectAll-string"
                                  : "selectAll-string"
                              }
                              placement="top"
                              disabled={isSelectAllDisabled}
                            >
                              <TouchableOpacity
                                style={[
                                  styles.selectAllButton,
                                  isSelectAllDisabled &&
                                    styles.selectAllButtonDisabled,
                                  !isSelectAllDisabled &&
                                    isSelectAllHovered &&
                                    styles.selectAllButtonHovered,
                                ]}
                                disabled={isSelectAllDisabled}
                                onPress={() =>
                                  toggleRowAllSelection(row.workouts)
                                }
                                onMouseEnter={() =>
                                  !isMobile && setHoveredCell(selectAllKey)
                                }
                                onMouseLeave={() => setHoveredCell(null)}
                              >
                                <Ionicons
                                  name={
                                    isAllSelected
                                      ? "checkmark-done"
                                      : "checkmark-done-outline"
                                  }
                                  size={18}
                                  color={
                                    isSelectAllDisabled
                                      ? Colors.grayColor
                                      : isAllSelected || isIndeterminate
                                        ? Colors.bubbleColor
                                        : Colors.grayColor
                                  }
                                  style={
                                    isIndeterminate ? { opacity: 0.5 } : {}
                                  }
                                />
                              </TouchableOpacity>
                            </Tooltip>
                          </View>

                          {weeks.map((week) => {
                            const workout = row.workouts.get(week);
                            const workoutId = workout?.id;
                            const isCurrentWorkout =
                              workoutId === currentWorkoutId;
                            const isBlockedWeek =
                              sourceProtocolSlot && blockedWeeks.has(week);
                            const isBlockedWorkout =
                              sourceProtocolSlot &&
                              blockedWorkouts.has(workoutId);
                            const isSelected =
                              workoutId && selectedWorkouts.has(workoutId);
                            // Check if blocked because another workout in this week is already selected
                            const isBlockedBySelection =
                              sourceProtocolSlot &&
                              weeksWithSelectedWorkouts.has(week) &&
                              !isSelected;
                            const cellKey = `${row.rowIndex}-${week}`;
                            const isHovered = hoveredCell === cellKey;
                            const hasWorkout = !!workout;
                            const isDisabled =
                              !hasWorkout ||
                              isCurrentWorkout ||
                              isBlockedWeek ||
                              isBlockedWorkout ||
                              isBlockedBySelection;
                            // Show tooltip only if blocked because another workout in this week is already selected
                            const shouldShowTooltip =
                              hasWorkout &&
                              isBlockedBySelection &&
                              !isCurrentWorkout;

                            return (
                              <View
                                key={cellKey}
                                style={[styles.gridCell, { width: CELL_SIZE }]}
                              >
                                <Tooltip
                                  content="duplicateExerciseProtocolSlotOnePerWeekTooltip-string"
                                  placement="top"
                                  disabled={!shouldShowTooltip}
                                >
                                  <TouchableOpacity
                                    style={[
                                      styles.checkboxContainer,
                                      !hasWorkout &&
                                        styles.checkboxContainerEmpty,
                                      isCurrentWorkout &&
                                        styles.checkboxContainerDisabled,
                                      (isBlockedWeek ||
                                        isBlockedWorkout ||
                                        isBlockedBySelection) &&
                                        styles.checkboxContainerBlocked,
                                    ]}
                                    disabled={isDisabled}
                                    onPress={() =>
                                      workoutId &&
                                      toggleWorkoutSelection(workoutId, week)
                                    }
                                    onMouseEnter={() =>
                                      !isMobile &&
                                      hasWorkout &&
                                      !isDisabled &&
                                      setHoveredCell(cellKey)
                                    }
                                    onMouseLeave={() => setHoveredCell(null)}
                                  >
                                    {!hasWorkout ? (
                                      <View style={styles.emptyCell} />
                                    ) : isCurrentWorkout ? (
                                      <View
                                        style={styles.currentWorkoutCheckbox}
                                      >
                                        <Ionicons
                                          name="checkmark"
                                          size={12}
                                          color={Colors.whiteColor}
                                        />
                                      </View>
                                    ) : isBlockedWeek ||
                                      isBlockedWorkout ||
                                      isBlockedBySelection ? (
                                      <View style={styles.blockedCheckbox}>
                                        <Ionicons
                                          name="remove"
                                          size={12}
                                          color={Colors.grayColor}
                                        />
                                      </View>
                                    ) : isSelected ? (
                                      <View style={styles.selectedCheckbox}>
                                        <Ionicons
                                          name="checkmark"
                                          size={12}
                                          color={Colors.whiteColor}
                                        />
                                      </View>
                                    ) : (
                                      <View
                                        style={[
                                          styles.unselectedCheckbox,
                                          isHovered &&
                                            styles.unselectedCheckboxHovered,
                                        ]}
                                      />
                                    )}
                                  </TouchableOpacity>
                                </Tooltip>
                              </View>
                            );
                          })}
                        </View>
                      );
                    })}

                    {/* Empty state when no workouts */}
                    {workoutRows.length === 0 && (
                      <View style={styles.emptyStateContainer}>
                        <Text style={styles.emptyStateText}>
                          {t("noWorkoutsInBlock-string")}
                        </Text>
                      </View>
                    )}
                  </View>
                </ScrollView>
              </ScrollView>
            </View>

            {sourceProtocolSlot && blockedWeeksList.length > 0 && (
              <View style={styles.blockedBanner}>
                <View style={styles.blockedBannerIcon}>
                  <Ionicons
                    name="information-circle-outline"
                    size={14}
                    color={Colors.darkgrayColor}
                  />
                </View>
                <View style={styles.blockedBannerContent}>
                  <Text style={styles.blockedBannerText}>
                    {sourceIsSecondaryProtocol
                      ? t(
                          "duplicateExerciseSecondaryProtocolSlotBlockedBody-string",
                          {
                            protocolSlot: protocolLabel,
                          },
                        )
                      : t(
                          blockedWeeksList.length === 1
                            ? "duplicateExerciseProtocolSlotBlockedBodySingular-string"
                            : "duplicateExerciseProtocolSlotBlockedBody-string",
                          {
                            weeks: blockedWeeksLabel,
                            protocolSlot: protocolSlotLabel || protocolLabel,
                          },
                        )}
                  </Text>
                </View>
              </View>
            )}

            <View style={styles.optionsContainer}>
              <View style={styles.optionToggleRow}>
                <ToggleSwitch
                  value={keepSameOrder}
                  onChange={() => setKeepSameOrder((prev) => !prev)}
                />
                <View style={styles.optionTextContainer}>
                  <Text style={styles.optionText}>
                    {t("duplicateExerciseKeepOrder-string")}
                  </Text>
                  <Text style={styles.optionHint}>
                    {keepSameOrder
                      ? t("duplicateExerciseKeepOrderHint-string", {
                          order: (exercise?.order ?? 0) + 1,
                        })
                      : t("duplicateExerciseAppendOrderHint-string")}
                  </Text>
                </View>
              </View>
            </View>

            {/* <View style={styles.infoBanner}>
              <View style={styles.infoAccent} />
              <Text style={styles.infoText}>
                {t("duplicateExerciseInfo-string")}
              </Text>
            </View> */}

            <View style={styles.actionsRow}>
              <TouchableOpacity
                style={[
                  styles.secondaryButton,
                  !hasSelection && styles.secondaryButtonDisabled,
                  hasSelection &&
                    clearAllHovered &&
                    styles.secondaryButtonHovered,
                ]}
                disabled={!hasSelection}
                onPress={() => setSelectedWorkouts(new Set())}
                onMouseEnter={() => !isMobile && setClearAllHovered(true)}
                onMouseLeave={() => setClearAllHovered(false)}
              >
                <Text
                  style={[
                    styles.secondaryText,
                    !hasSelection && styles.secondaryTextDisabled,
                  ]}
                >
                  {t("clearAll-string")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.primaryButton,
                  !hasSelection && { opacity: 0.6 },
                  hasSelection && confirmHovered && styles.primaryButtonHovered,
                ]}
                disabled={!hasSelection}
                onPress={handleDuplicate}
                onMouseEnter={() => !isMobile && setConfirmHovered(true)}
                onMouseLeave={() => setConfirmHovered(false)}
              >
                <Text style={styles.primaryText}>
                  {t("duplicateExerciseConfirm-string")}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  },
);

const styles = StyleSheet.create({
  modalView: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    padding: 24,
    width: "auto",
    maxWidth: "90%",
    alignSelf: "center",
    zIndex: 1000,
    elevation: 10,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    flexDirection: "column",
    maxHeight: "100%",
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    cursor: "pointer",
  },
  title: {
    ...Fonts.bold18,
    color: Colors.blackColor,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: "500",
    color: Colors.bubbleColor,
    // marginTop: 4, // 4px gap
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.darkgrayColor,
    marginTop: 8, // 8px below label
  },
  gridSectionContainer: {
    marginBottom: 0,
    flexShrink: 1,
    minHeight: 0,
  },
  gridSectionContainerMobile: {
    flex: 1,
  },
  verticalScrollView: {
    maxHeight: 600,
  },
  verticalScrollViewMobile: {
    maxHeight: undefined,
    flex: 1,
    minHeight: 0,
  },
  verticalScrollContent: {
    flexGrow: 1,
  },
  gridScrollView: {
    flex: 1,
  },
  gridScrollContent: {
    paddingBottom: 0,
  },
  gridScrollContentCentered: {
    flexGrow: 1,
    justifyContent: "center",
  },
  gridContainer: {
    flexDirection: "column",
  },
  gridHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
  },
  cornerCell: {
    height: 32,
  },
  weekHeaderCell: {
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    marginHorizontal: 4,
  },
  weekHeaderText: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.grayColor,
    textAlign: "center",
  },
  gridRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  workoutLabelCell: {
    height: 36,
    justifyContent: "center",
    paddingRight: 8,
    flexShrink: 0,
  },
  workoutLabelContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
  workoutLabelWord: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.blackColor,
    flexShrink: 1,
  },
  workoutLabelNumber: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.blackColor,
    flexShrink: 0,
  },
  gridCell: {
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    marginHorizontal: 4,
  },
  checkboxContainer: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
  },
  checkboxContainerEmpty: {
    opacity: 0.3,
  },
  checkboxContainerDisabled: {
    opacity: 0.6,
  },
  checkboxContainerBlocked: {
    opacity: 0.85,
  },
  emptyCell: {
    width: 18,
    height: 18,
    borderRadius: 4,
    backgroundColor: Colors.borderColor,
  },
  currentWorkoutCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    backgroundColor: Colors.grayColor,
    alignItems: "center",
    justifyContent: "center",
  },
  selectedCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    backgroundColor: Colors.bubbleColor,
    alignItems: "center",
    justifyContent: "center",
  },
  unselectedCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
  },
  unselectedCheckboxHovered: {
    borderColor: Colors.bubbleColor,
  },
  blockedCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    backgroundColor: Colors.lightGrayColor,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyStateContainer: {
    paddingVertical: 32,
    alignItems: "center",
  },
  emptyStateText: {
    fontSize: 13,
    color: Colors.grayColor,
  },
  optionsContainer: {
    marginTop: 16, // 16px gap
  },
  optionToggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    paddingVertical: 4, // Slightly more padding
  },
  optionTextContainer: {
    flex: 1,
    gap: 4, // 4px vertical gap
  },
  optionText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  optionHint: {
    fontSize: 11,
    color: Colors.grayColor,
    lineHeight: 14,
  },
  infoBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8, // 8px horizontal gap
    marginTop: 16,
  },
  infoAccent: {
    width: 3,
    alignSelf: "stretch",
    backgroundColor: Colors.grayColor,
    borderRadius: 4,
    marginRight: 0,
    marginTop: 2,
    marginBottom: 2,
  },
  infoText: {
    flex: 1,
    fontSize: 11,
    lineHeight: 15,
    color: Colors.grayColor,
  },
  blockedBanner: {
    marginTop: 12, // Reduced from 12px
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: Colors.lightGrayColor,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  blockedBannerIcon: {
    paddingTop: 1,
  },
  blockedBannerContent: {
    flex: 1,
    gap: 0,
  },
  blockedBannerTitle: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  blockedBannerText: {
    fontSize: 11,
    lineHeight: 15,
    color: Colors.mediumGray,
  },
  selectAllButton: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
  },
  selectAllButtonHovered: {
    backgroundColor: Colors.backgroundColor,
  },
  selectAllButtonDisabled: {
    opacity: 0.5,
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12, // 12px gap
    marginTop: 24, // 24px top margin
  },
  secondaryButton: {
    height: 36,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.whiteColor,
  },
  secondaryButtonHovered: {
    backgroundColor: Colors.backgroundColor,
  },
  secondaryButtonDisabled: {
    opacity: 0.5,
  },
  secondaryText: {
    fontSize: 14,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  secondaryTextDisabled: {
    color: Colors.grayColor,
  },
  primaryButton: {
    height: 36,
    paddingHorizontal: 18,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    backgroundColor: Colors.bubbleColor,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonHovered: {
    opacity: 0.9,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: "600",
    color: Colors.whiteColor,
  },
});

export default DuplicateExerciseModal;
